
import { Component, ChangeDetectionStrategy, ViewEncapsulation, Input, OnChanges, SimpleChanges, OnDestroy, Output, EventEmitter, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MasterDetailEditorBodyDirective, MasterDetailEditorComponent, MasterDetailEditorDisplayMode, MasterDetailEditorLeftActionsDirective, MasterDetailEditorRightActionsDirective } from '@workbuddy/ui/blocks/master.detail.editor';
import { AttachmentComponent, canEditFormAttachment } from '@workbuddy/ui/components/attachment';
import { ActivityEntityName, Attachment, DialogType, MetadataAttachment, getExtension, getFileThumbnailUrl, getFilenameWithoutExtension, isPortal } from '@jms/model';
import { ActiveService, DialogService, SelectionService, WorkBuddyDialogService } from '@workbuddy/ui/services';
import { ButtonComponent } from '@workbuddy/ui/components/button';
import { Command } from '@workbuddy/ui/model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FilesContext, FilesDisplayMode } from '../../model';
import { LuxonCalendarModule } from '@workbuddy/ui/fields/luxon.calendar';
import { AttachmentsComponent } from '@workbuddy/ui/components/attachments';
import * as uuid from 'uuid';
import { UrlHelper } from '@jms/utils/util.helper';
import { AttachmentContext } from '@workbuddy/ui/components/attachment/model';
import { CurrentUserService } from '@jms/ui/ui.standalone.services/current.user.service';
import { AttachmentFormEditorComponent } from '@workbuddy/ui/components/attachment/editor/attachment.form.editor.component';
import { tap } from 'rxjs';
import { ConfigService } from '@jms/ui/ui.standalone.services/config.service';;
import { CallPipe } from 'ngxtension/call-apply';

@UntilDestroy()
@Component({
    selector: 'file-details-preview',
    templateUrl: 'file.details.preview.component.html',
    styleUrls: ['file.details.preview.component.less'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CommonModule,
        AttachmentsComponent,
        ButtonComponent,
        LuxonCalendarModule,
        MasterDetailEditorComponent,
        MasterDetailEditorLeftActionsDirective,
        MasterDetailEditorRightActionsDirective,
        MasterDetailEditorBodyDirective,
        CallPipe
    ]
})
export class FileDetailsPreviewComponent implements OnChanges, OnDestroy {

    @Input() public context: FilesContext;
    @Input() public attachment: MetadataAttachment;
    @Input() public attachmentContext: AttachmentContext;

    @Output() public delete = new EventEmitter<Attachment>();

    private currentUser = inject(CurrentUserService);
    private dialogs = inject(WorkBuddyDialogService);

    public attachments: MetadataAttachment[];

    protected editCommand: Command;

    protected get entity(): ActivityEntityName {
        return this?.context?.entity;
    }

    protected get timezone(): string {
        return this?.context?.timezone ?? 'utc';
    }

    protected toggleAddToDownloadsCommand = new Command({
        label: 'Add to download list',
        iconLeft: 'fa-plus',
        action: () => this.toggleAddToDownloads()
    });

    protected toggleAddToSelectionCommand = new Command({
        label: 'Add to selection',
        iconLeft: 'fa-plus',
        action: () => this.toggleAddToDownloads()
    });

    protected display = MasterDetailEditorDisplayMode.Default;
    protected readonly MasterDetailEditorDisplayMode = MasterDetailEditorDisplayMode;
    protected readonly DialogType = DialogType;
    protected readonly FilesDisplayMode = FilesDisplayMode;

    constructor(
        private activeService: ActiveService<AttachmentComponent>,
        private selectionService: SelectionService<string>,
        private dialogService: DialogService,
        private cdr: ChangeDetectorRef
    ) {

        // this.selectionService
        //     .selection$
        //     .pipe(
        //         untilDestroyed(this),
        //         filter(_ => !!this.attachment),
        //         tap(_ => this.updateToggleAddToDownloadsCommand())
        //     )
        //     .subscribe();

    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.attachment) {
            this.updateToggleCommands();

            this.attachments = this.previewAttachments;
            this.updateEditCommands();
        }
    }

    ngOnDestroy(): void { }

    protected toggleExpand() {

        switch (this.context.displayMode) {
            case FilesDisplayMode.Full:
                this.dialogService.dismissModal();
                break;
            case FilesDisplayMode.Sidebar:
                this.dialogService.showModalDialog(DialogType.Edit);
                break;
        }
    }

    protected dismiss(): void {
        this.dialogService.dismissModal();
    }

    protected toggleAddToDownloads(): void {
        //this.selectionService.toggle(this.activeService.activeItem);
        this.activeService?.activeItem?.toggleSelection();
        this.updateToggleCommands();
    }

    protected get canMoveNext(): boolean {
        return this.activeService.keyManager.activeItemIndex < this.activeService.items.length - 1;
    }

    protected get canMovePrev(): boolean {
        return this.activeService.keyManager.activeItemIndex > 0;
    }

    protected get canExpand(): boolean {
        return this.context.displayMode !== FilesDisplayMode.Picker;
    }

    protected get previewAttachments(): Attachment[] {
        let attachments: Attachment[] = [];

        switch (this.attachment.type) {
            case 'Form':
                attachments = [
                    this.attachment,
                    this.getFormFilePreviewAttachment(),
                ];
                break;
            case 'File':
                if (this.attachment.kind === 'Form') {
                    attachments = [
                        this.getFileFormPreviewAttachment(),
                        this.attachment,
                    ];
                } else {
                    attachments = [this.attachment]
                }
                break;
            default:
                attachments = [this.attachment];
                break;
        }

        return this.filterAttachments(attachments);
    }

    private filterAttachments(attachments: Attachment[]): Attachment[] {
        return attachments.filter(x => !!x);
    }

    /**
     * Get the fake attachment of the form that was used to generate the PDF file.
     * 
     * This attachment need to have base64 encoded data which include form response's id
     * @returns Attachment
     */
    private getFileFormPreviewAttachment(): Attachment {
        if (this.attachment.kind !== 'Form' || this.attachment.type !== 'File') {
            return null;
        }

        const path = this.attachment.path;
        let rId, data;
        if (getExtension(path).includes("pdf")) {
            rId = getFilenameWithoutExtension(path);
            data = UrlHelper.serializeParams({
                rId,
            });
        }

        if (!rId) {
            return null;
        }

        return {
            _id: rId,
            name: this.attachment.title,
            type: 'Form',
            path: this.attachment.path,
            data,
            thumbnail: this.attachment.thumbnail,
        } as Attachment;
    }

    /**
     * Get the fake attachment of the PDF file that was generated from the form.
     * 
     * This attachment need to have the path of the PDF file and type as File.
     * @returns Attachment
     */
    private getFormFilePreviewAttachment(): Attachment {
        if (this.attachment.type !== 'Form') {
            return null;
        }

        const path = this.attachment.path;
        if (!getExtension(path).includes("pdf")) {
            return null;
        }

        return {
            _id: uuid.v4(),
            name: this.attachment.title,
            type: 'File',
            path: this.attachment.path,
            thumbnail: this.attachment.thumbnail,
        } as Attachment;
    }

    protected moveNext(): void {
        this.activeService.keyManager.setNextItemActive();
    }

    protected movePrev(): void {
        this.activeService.keyManager.setPreviousItemActive();
    }

    private updateToggleCommands(): void {
        const isSelected = this.activeService?.activeItem?.selected;

        if (this.context.displayMode !== FilesDisplayMode.Picker && canEditFormAttachment(this.attachment, this.attachmentContext.right, this.currentUser.user)) {
            this.editCommand = new Command({
                label: 'Edit',
                iconLeft: 'fa-edit',
                action: () => this.onEdit(),
            });
        }


        this.toggleAddToDownloadsCommand.iconLeft = isSelected
            ? 'fa-minus'
            : 'fa-plus';

        this.toggleAddToDownloadsCommand.label = !isSelected
            ? 'Add to download list'
            : 'Remove from download list';

        this.toggleAddToSelectionCommand.iconLeft = isSelected
            ? 'fa-minus'
            : 'fa-plus';

        this.toggleAddToSelectionCommand.label = !isSelected
            ? 'Add to selection'
            : 'Remove from selection';
    }

    private onEdit(): void {
        if (this.attachment.kind == 'Form' || this.attachment.type == 'Form') {
            this.onEditForm();
        }
    }

    private onEditForm(): void {
        const data = {
            attachment: this.attachment,
        }

        this.dialogs
            .open(AttachmentFormEditorComponent, { data })
            .onClose
            .pipe(
                untilDestroyed(this),
                tap(response => {
                    if (response) {
                        const attachment = {
                            ...this.attachment,
                            updatedAt: new Date(),
                        } as Attachment;
                        attachment.thumbnail = getFileThumbnailUrl(attachment, true);

                        this.attachment = attachment;

                        this.attachments = this.previewAttachments;

                        this.cdr.markForCheck();

                    }
                }),
            ).subscribe();
    }

    public safeAttachments(attachments: Attachment[]): Attachment[] {
        return isPortal(ConfigService.environment.domainType as any)
            ? attachments.filter(a => a.type !== 'Form')
            : attachments;
    }

    protected updateEditCommands(): void {
        if (this.context.displayMode !== FilesDisplayMode.Picker && this.canEdit(this.attachment)) {
            this.editCommand = new Command({
                label: 'Edit',
                iconLeft: 'fa-edit',
                action: () => this.onEdit(),
            });
        } else {
            this.editCommand = null;
        }
    }

    protected canEdit(attachment: Attachment) {
        return canEditFormAttachment(attachment, this.attachmentContext.right, this.currentUser.user);
    }
}